import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { CardView } from "../../components/card/CardView";
import { api, errorMessage } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { usePublicConfig, updateCachedProfileDesign } from "../../lib/config";
import { ProfileFieldOrder } from "./ProfileFieldOrder";
import { BackgroundPicker } from "./BackgroundPicker";

const LAYOUTS = [["classic", "Classic"], ["centered", "Centered"], ["banner", "Banner"]];
const SHAPES = [["rounded", "Rounded"], ["circle", "Circle"], ["square", "Square"]];
const COLORS = [["bg", "Background"], ["fg", "Text"], ["accent", "Accent"], ["accent_fg", "Accent text"]];
const SAMPLE = { phone: "+966 50 123 4567", whatsapp: "+966 50 123 4567", office_phone: "+966 11 234 5678", extension: "204",
  department_en: "Design", department_ar: "التصميم", office_location_en: "Riyadh HQ", office_location_ar: "المقر الرئيسي - الرياض",
  bio_en: "Designing brands people remember.", bio_ar: "أصمم علامات تجارية لا تُنسى." };
const sampleValue = (f) => SAMPLE[f.key] || (f.type === "url" ? `https://example.com/${f.key}` : f.type === "email" ? "name@example.com" : f.type === "tel" ? "+966 50 000 0000" : f.label_en);
const labelCls = "text-[11px] font-bold uppercase tracking-wider text-slate-500";

const Options = ({ items, value, onChange, testId }) => {
  const { t } = useLang();
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
          className={`btn-press rounded-xl border px-3 py-2 text-sm font-semibold ${value === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{t(l)}</button>
      ))}
    </div>
  );
};

const ordered = (fields, order) => {
  const rank = Object.fromEntries(order.map((k, i) => [k, i]));
  return [...fields].sort((a, b) => (rank[a.key] ?? order.length) - (rank[b.key] ?? order.length));
};

export const ProfileDesign = () => {
  const { t } = useLang();
  const config = usePublicConfig();
  const [d, setD] = useState(null);
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/admin/settings/profile-design"), api.get("/fields")])
      .then(([a, b]) => { setD(a.data.design); setFields(b.data.fields.filter((f) => f.enabled)); })
      .catch((e) => toast.error(errorMessage(e)));
  }, []);
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));
  const list = useMemo(() => (d ? ordered(fields, d.order) : []), [fields, d]);
  const toggle = (key, show) => set("hidden")(show ? d.hidden.filter((k) => k !== key) : [...d.hidden, key]);
  const sample = useMemo(() => d && ({ slug: "preview", name_en: "Sara Ahmed", name_ar: "سارة أحمد", title_en: "Senior Designer", title_ar: "مصممة أولى",
    email: "sara@inception.sa", theme: "inception-black", bg_image: true, photo_url: "", fields: list.filter((f) => !d.hidden.includes(f.key)).map((f) => ({ ...f, value: sampleValue(f) })) }), [list, d]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/profile-design", { ...d, order: list.map((f) => f.key) });
      setD(data.design); updateCachedProfileDesign(data.design); toast.success(t("Profile design saved"));
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  if (!d) return <div className="h-48 animate-pulse rounded-3xl bg-slate-200" />;
  return (
    <div className="space-y-4 rounded-3xl border border-slate-200/80 bg-white p-6" data-testid="profile-design">
      <div className="flex flex-wrap items-center gap-3">
        <div className="me-auto">
          <h2 className="font-display text-xl font-bold text-slate-900">{t("Profile design")}</h2>
          <p className="text-sm text-slate-500">{t("How everyone's public card page looks when people scan the QR code.")}</p>
        </div>
        <button type="button" onClick={save} disabled={saving} data-testid="profile-design-save"
          className="btn-press min-h-[42px] rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white disabled:opacity-60">{saving ? t("Saving…") : t("Save design")}</button>
      </div>
      <div className="flex flex-col gap-8 lg:flex-row">
        <div className="w-full shrink-0 rounded-3xl bg-alabaster p-4 lg:w-[400px]" data-testid="profile-preview">
          <div className="pointer-events-none"><CardView card={sample} config={config} design={d} /></div>
        </div>
        <div className="flex-1 space-y-5">
          <div className="space-y-1.5"><p className={labelCls}>{t("Layout")}</p><Options items={LAYOUTS} value={d.layout} onChange={set("layout")} testId="profile-layout" /></div>
          <div className="space-y-1.5"><p className={labelCls}>{t("Photo shape")}</p><Options items={SHAPES} value={d.photo_shape} onChange={set("photo_shape")} testId="profile-shape" /></div>
          <div className="space-y-1.5"><p className={labelCls}>{t("Background image")}</p>
            <BackgroundPicker d={d} onOverlay={set("bg_overlay")} onDesign={(nd) => { setD((x) => ({ ...x, bg_image_url: nd.bg_image_url, bg_version: nd.bg_version })); updateCachedProfileDesign(nd); }} />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Switch checked={d.colors === "custom"} onCheckedChange={(v) => set("colors")(v ? "custom" : "card")} data-testid="profile-custom-colors" />
              {t(d.colors === "custom" ? "Custom colours" : "Use each employee's card colour")}
            </label>
            {d.colors === "custom" && (
              <div className="flex flex-wrap gap-3">
                {COLORS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                    <input type="color" value={d[k]} onChange={(e) => set(k)(e.target.value)} className="h-8 w-8 cursor-pointer rounded border-0 p-0" data-testid={`profile-color-${k}`} /> {t(l)}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <p className={labelCls}>{t("Fields on the profile")}</p>
            <p className="text-xs text-slate-400">{t("Turn fields on or off and drag to set their order. Empty fields are skipped on each person's card.")}</p>
            <ProfileFieldOrder list={list} hidden={d.hidden} onOrder={set("order")} onToggle={toggle} />
          </div>
        </div>
      </div>
    </div>
  );
};
